/**
 * PanZoomCanvas — A4 페이지 캔버스 + 줌 + 손바닥 팬.
 *
 * 동작:
 *  - 휠 (modifier 없이): 일반 스크롤 (브라우저 기본 동작 — overflow:auto)
 *  - Ctrl + 휠 / Cmd + 휠: 확대/축소
 *  - 빈 영역 마우스 드래그: 화면을 잡고 끌어 이동 (scrollLeft/scrollTop 조작)
 *  - 모바일: 한 손가락 = 자연스러운 터치 스크롤 / 두 손가락 핀치 = 줌
 *  - 우상단 툴바: -, %, +, 화면맞춤
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** 외부 툴바에서 줌을 제어할 수 있도록 노출하는 imperative API */
export interface PanZoomHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  reset100: () => void;
  fit: () => void;
}

interface Props {
  children: ReactNode;
  contentWidth: number;
  contentHeight: number;
  /** 고정 max-height (예: "75vh"). fillParent 가 있으면 무시 */
  maxHeight?: string;
  /** 부모의 남은 공간을 모두 채움 (flex-1 min-h-0 컨테이너 안에서) — 페이지 외부 스크롤 제거 */
  fillParent?: boolean;
  background?: string;
  /** 캔버스 위 floating 툴바 숨김 (외부에서 ref 로 제어) */
  hideToolbar?: boolean;
  /** 현재 zoom 값 변경 알림 — 외부 툴바에서 % 표시용 */
  onZoomChange?: (zoom: number) => void;
}

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const touchDist = (a: React.Touch, b: React.Touch) =>
  Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

/**
 * 클릭 지점이 "편집/조작" 요소인지 — TRUE 면 팬 비활성, 클릭이 그대로 통과해 정상 편집/클릭.
 * - 실제 입력 컨트롤 (input/textarea/select/button/a): 항상 true
 * - 변수 pill / positioned 텍스트 / 필드 박스 안: 편집 영역으로 간주 — true
 * - contractPage 의 흰 종이 빈 영역 (positioned 박스 사이의 여백) / PanZoomCanvas 의 회색 캔버스 배경: false (팬 가능)
 */
// (isInteractive 헬퍼 — 회색 캔버스 배경 vs 흰 페이지 내부 판별이 필요해질 때 재추가)

export const PanZoomCanvas = forwardRef<PanZoomHandle, Props>(({
  children,
  contentWidth,
  contentHeight,
  maxHeight = "75vh",
  fillParent = false,
  background = "#E5E7EB",
  hideToolbar = false,
  onZoomChange,
}, ref) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [fitZoom, setFitZoom] = useState(1);
  const [naturalH, setNaturalH] = useState(contentHeight);
  const [containerW, setContainerW] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [userAdjustedZoom, setUserAdjustedZoom] = useState(false);
  // setHandTool 은 toggle 핸들러가 빠진 상태 — handTool 만 read-only 로 사용
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [handTool, _setHandTool] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);

  /* 컨테이너 크기 + 콘텐츠 자연 높이 측정 — fit-zoom 자동 계산용 */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const recompute = () => {
      setContainerW(el.clientWidth);
      const inner = innerRef.current;
      if (inner) setNaturalH(inner.scrollHeight || contentHeight);
      const w = el.clientWidth - 16;
      setFitZoom(Math.min(Math.max(0.1, w / contentWidth), 1));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    if (innerRef.current) ro.observe(innerRef.current);
    window.addEventListener("resize", recompute);
    return () => { ro.disconnect(); window.removeEventListener("resize", recompute); };
  }, [contentWidth, contentHeight, children]);

  /* 사용자가 zoom 직접 조정 안 했으면 fit 따라가기 */
  useEffect(() => {
    if (!userAdjustedZoom) setZoom(fitZoom);
  }, [fitZoom, userAdjustedZoom]);

  /* 콘텐츠 크기 (zoom 반영) — wrapper 높이 = 정확히 그만큼 → 빈 스크롤 영역 없음 */
  const scaledW = contentWidth * zoom;
  const scaledH = naturalH * zoom;

  /* Ctrl + 휠 → 줌 (네이티브 wheel listener — passive: false 로 등록해서 preventDefault 가능)
   * 일반 휠은 브라우저 기본 스크롤 유지 */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left + el.scrollLeft;
      const my = e.clientY - rect.top + el.scrollTop;
      const cx = mx / zoom;
      const cy = my / zoom;
      const delta = -e.deltaY * 0.0015;
      const next = clamp(zoom + delta * zoom, ZOOM_MIN, ZOOM_MAX);
      if (next === zoom) return;
      setZoom(next);
      setUserAdjustedZoom(true);
      requestAnimationFrame(() => {
        if (!el) return;
        const newScrollLeft = cx * next - (e.clientX - rect.left);
        const newScrollTop = cy * next - (e.clientY - rect.top);
        el.scrollLeft = Math.max(0, newScrollLeft);
        el.scrollTop = Math.max(0, newScrollTop);
      });
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [zoom]);

  /* 스페이스 키 → 손바닥 모드 강제 (편집 영역 위에서도 드래그 가능) */
  useEffect(() => {
    const onKD = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        const t = e.target as HTMLElement | null;
        // input/textarea/contentEditable 안에서는 스페이스가 띄어쓰기로 사용 — 그때는 무시
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
        e.preventDefault();
        setSpacePressed(true);
      }
    };
    const onKU = (e: KeyboardEvent) => { if (e.code === "Space") setSpacePressed(false); };
    window.addEventListener("keydown", onKD);
    window.addEventListener("keyup", onKU);
    return () => { window.removeEventListener("keydown", onKD); window.removeEventListener("keyup", onKU); };
  }, []);

  /* 자동 손바닥 팬 — 콘텐츠가 컨테이너를 넘어서면 페이지 어디서든 드래그하면 화면이 따라감.
     클릭(움직임 < 5px) 은 정상 편집/선택, 드래그(움직임 ≥ 5px) 만 팬으로 인식.
     native capture-phase 리스너 — TipTap/ProseMirror 보다 먼저 잡아 드래그 시 텍스트 선택을 취소. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const DRAG_THRESHOLD = 5; // 픽셀

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0 && e.button !== 1) return;
      const target = e.target as HTMLElement;

      // Ctrl / Shift / Cmd(Meta) 누른 채 드래그 = 텍스트 선택 (브라우저/PM 기본 동작 양보)
      if (e.shiftKey || e.ctrlKey || e.metaKey) return;

      // 필드 박스(서명/도장/체크/텍스트/날짜) 위 — 항상 필드 자체의 드래그 핸들러
      // (Step 2 오버레이는 data-field-id, 에디터 인라인 필드 노드는 data-contract-field)
      if (target.closest('[data-contract-field], [data-field-id]')) return;

      // 편집 가능한 에디터(본문작성) 위 — 드래그는 텍스트/변수 선택에 양보.
      // 손바닥 모드(스페이스/핸드툴)일 때만 팬. 필드설정·검토는 에디터가
      // readOnly(contenteditable=false)라 이 분기를 타지 않고 기존 팬 유지.
      if (!(handTool || spacePressed) && target.closest('[contenteditable="true"]')) return;

      // 명확한 입력 컨트롤 — 정상 클릭 동작
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" || target.tagName === "BUTTON" || target.tagName === "A") {
        return;
      }

      // 그 외 모든 영역(흰 페이지 텍스트 위 포함) — 드래그 시 손바닥 팬 동작
      // mousedown 즉시 isPanning=true 로 전환해 커서를 손바닥으로 변경 (사용자 요청).
      // 클릭만 하고 떼면 (5px 미만 이동) onUp 에서 정상 복구되며, ProseMirror 의 cursor placement 도
      // mousedown 이벤트가 propagate 되어 정상 동작.
      setIsPanning(true);

      const sx = e.clientX, sy = e.clientY;
      const sl0 = el.scrollLeft, st0 = el.scrollTop;
      let started = false;

      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - sx;
        const dy = ev.clientY - sy;
        if (!started) {
          // 드래그 의도 감지 (5px 이동 시) — 실제 scroll 은 충분한 이동 후에만
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
          started = true;
          // 드래그 시작 시점의 누적된 텍스트 선택 제거 (의도된 클릭이 아닌 드래그)
          window.getSelection()?.removeAllRanges();
        }
        ev.preventDefault();
        el.scrollLeft = sl0 - dx;
        el.scrollTop = st0 - dy;
      };
      const onUp = () => {
        setIsPanning(false);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    };

    el.addEventListener("mousedown", onDown, { capture: true });
    return () => {
      el.removeEventListener("mousedown", onDown, { capture: true } as any);
    };
  }, [handTool, spacePressed]);

  /* 콘텐츠 overflow 추적 — 자동 손바닥 커서 표시용 */
  const [hasOverflow, setHasOverflow] = useState(false);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoom, naturalH, containerW]);

  /* 터치 — 1 손가락 = 브라우저 자연 스크롤 / 2 손가락 = 핀치 줌 */
  const pinchRef = useRef<{ d0: number; z0: number } | null>(null);
  const touchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      pinchRef.current = { d0: touchDist(e.touches[0], e.touches[1]), z0: zoom };
    }
  };
  const touchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const d = touchDist(e.touches[0], e.touches[1]);
      const next = clamp((d / pinchRef.current.d0) * pinchRef.current.z0, ZOOM_MIN, ZOOM_MAX);
      setZoom(next);
      setUserAdjustedZoom(true);
    }
  };
  const touchEnd = () => { pinchRef.current = null; };

  const fitToScreen = () => { setUserAdjustedZoom(false); };
  const zoomTo100 = () => {
    setUserAdjustedZoom(true);
    setZoom(1);
    const el = scrollRef.current;
    if (el) { el.scrollLeft = 0; el.scrollTop = 0; }
  };
  const zoomDelta = (d: number) => {
    setUserAdjustedZoom(true);
    setZoom((z) => clamp(z + d, ZOOM_MIN, ZOOM_MAX));
  };

  /* 외부 툴바에서 ref 로 호출할 imperative API */
  useImperativeHandle(ref, () => ({
    zoomIn: () => zoomDelta(0.1),
    zoomOut: () => zoomDelta(-0.1),
    reset100: zoomTo100,
    fit: fitToScreen,
  }), [zoom]);

  /* zoom 변화 외부로 알림 (toolbar % 표시) */
  useEffect(() => { onZoomChange?.(zoom); }, [zoom, onZoomChange]);

  const handMode = handTool || spacePressed;
  // 커서 정책:
  //  - panning 중       : 손바닥 (grabbing) — 전 영역에 강제
  //  - handTool/Space   : 손바닥 미리보기 (grab)
  //  - 그 외(평소)       : auto — 컨텐츠가 결정 (텍스트 위는 I-beam, 회색 배경은 default)
  const cursor = isPanning ? "grabbing" : handMode ? "grab" : "auto";

  return (
    <div className={`relative ${fillParent ? "flex-1 min-h-0 flex flex-col" : ""}`}>
      {/* 줌 floating 툴바 — hideToolbar 면 숨김 (외부 툴바에서 ref 로 제어) */}
      {!hideToolbar && (
        <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-white/90 backdrop-blur border border-[#E9E9E9] rounded-full shadow px-1 py-0.5 text-[12px]">
          <button type="button" onClick={() => zoomDelta(-0.1)} className="w-7 h-7 hover:bg-[#F5F5F5] rounded-full" title="축소">−</button>
          <button type="button" onClick={zoomTo100} className="px-2 h-7 hover:bg-[#F5F5F5] rounded-full font-mono" title="100%">{Math.round(zoom * 100)}%</button>
          <button type="button" onClick={() => zoomDelta(0.1)} className="w-7 h-7 hover:bg-[#F5F5F5] rounded-full" title="확대">+</button>
          <span className="w-px h-4 bg-[#E9E9E9] mx-0.5" />
          <button type="button" onClick={fitToScreen} className="px-2 h-7 hover:bg-[#F5F5F5] rounded-full" title="화면 맞춤">⤢</button>
        </div>
      )}

      {/* 손바닥 모드 또는 자동 overflow 시 안내 */}
      {(handMode || (hasOverflow && isPanning)) && (
        <div className="absolute top-2 left-2 z-20 text-[10px] text-[#666] bg-white/80 backdrop-blur rounded-full px-2 py-1 pointer-events-none">
          ✋ 드래그로 이동
        </div>
      )}

      <div
        ref={scrollRef}
        className={`rounded-md ${fillParent ? "flex-1 min-h-0" : ""}`}
        style={{
          background,
          ...(fillParent ? {} : { maxHeight }),
          overflow: "auto",
          cursor,
        }}
        onTouchStart={touchStart}
        onTouchMove={touchMove}
        onTouchEnd={touchEnd}
      >
        {/* 스케일된 박스 크기를 차지하는 wrapper — 빈 스크롤 영역 발생 안 함.
            ⚠ 자식은 transform: scale 만 적용된 1배율(naturalW=794) layout 공간을 차지.
               wrapper 가 scaledW(예: 421) 만이라 자식의 layout overflow 가 부모의 overflow:auto
               에 잡혀 가짜 가로 스크롤바가 생김 → overflow: hidden 으로 layout overflow 차단. */}
        <div
          style={{
            width: `${scaledW}px`,
            height: `${scaledH}px`,
            margin: "12px auto",
            overflow: "hidden",
          }}
        >
          <div
            ref={innerRef}
            style={{
              width: `${contentWidth}px`,
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
              userSelect: isPanning ? "none" : "auto",
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
});
